import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { FormattedMessage, defineMessages } from 'react-intl';

import { useTrigger } from '@atlassian/post-office-context/post-office-trigger';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { Text } from '@atlaskit/primitives/text';

import {
	PlacementFrontend,
	type placementRenderProps,
} from '@post-office/browser--incident-management/jsm-responders-update';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

const messages = defineMessages({
	spotlightHeading: {
		id: 'post-office.jsm-responders-update.spotlight-heading',
		defaultMessage: 'Find the right team, faster',
		description: 'Heading for responder search spotlight.',
	},
	spotlightBody: {
		id: 'post-office.jsm-responders-update.spotlight-body',
		defaultMessage:
			'Search by operations or service names and descriptions to quickly connect with the right team.',
		description: 'Description for responder search spotlight.',
	},
	spotlightButton: {
		id: 'post-office.jsm-responders-update.spotlight-button',
		defaultMessage: 'OK',
		description: 'OK button to dismiss spotlight.',
	},
});

// Frontend
const render = PlacementFrontend.createFrontendStage({
	type: 'render',

	validate: noValidation<placementRenderProps>(),

	Component({ dismiss }) {
		const { onClosed, onAcknowledged } = usePostOfficeUserInteractions();
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

		const dismissSpotlight = useCallback(() => {
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'jsmRespondersSearchSpotlightDismiss',
			});
			dismiss();
		}, [dismiss, fireAnalyticsEvent]);

		const handleDismiss = useCallback(() => {
			void onClosed({ interactionIdentifier: 'jsmRespondersUpdateClose', additionalMetadata: {} });
			dismissSpotlight();
		}, [dismissSpotlight, onClosed]);

		const handleAcknowledged = useCallback(() => {
			void onAcknowledged({
				interactionIdentifier: 'jsmRespondersUpdateOk',
				additionalMetadata: {},
			});
			dismissSpotlight();
		}, [dismissSpotlight, onAcknowledged]);

		const { isActive: isTriggerActive } = useTrigger(
			'responders-search-enhancement-spotlight-trigger',
		);

		if (!isTriggerActive) {
			return null;
		}

		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* Dummy target */}
					<div />
				</PopoverTarget>
				<PopoverContent
					dismiss={handleDismiss}
					placement="bottom-end"
					isVisible={true}
					shouldDismissOnClickOutside={false}
				>
					<SpotlightCard>
						<SpotlightHeader>
							<SpotlightHeadline>
								<FormattedMessage {...messages.spotlightHeading} />
							</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl onClick={handleDismiss} />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightBody>
							<Text>
								<FormattedMessage {...messages.spotlightBody} />
							</Text>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={handleAcknowledged}>
									<FormattedMessage {...messages.spotlightButton} />
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<placementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
